/**
 * Ruler components for the template editor canvas
 * Displays measurement marks and labels in mm
 */

import { useMemo } from 'react';

interface RulerProps {
    length: number;
    scale: number;
    offset: number;
    containerSize: number;
}

function getTickInterval(scale: number): { major: number; minor: number } {
    if (scale < 1) return { major: 100, minor: 50 };
    if (scale < 3) return { major: 50, minor: 25 };
    if (scale < 6) return { major: 25, minor: 10 };
    if (scale < 12) return { major: 25, minor: 5 };
    if (scale < 20) return { major: 10, minor: 5 };
    if (scale < 40) return { major: 5, minor: 1 };
    return { major: 2, minor: 1 };
}

export function HorizontalRuler({ length, scale, offset, containerSize }: RulerProps) {
    const ticks = useMemo(() => {
        const { major, minor } = getTickInterval(scale);
        const result: { pos: number; label: string | null; isMajor: boolean }[] = [];

        for (let mm = 0; mm <= length; mm += minor) {
            const isMajor = mm % major === 0;
            const pixelPos = offset + mm * scale;

            if (pixelPos >= -20 && pixelPos <= containerSize + 20) {
                result.push({
                    pos: pixelPos,
                    label: isMajor ? String(mm) : null,
                    isMajor
                });
            }
        }

        return result;
    }, [length, scale, offset, containerSize]);

    return (
        <div className="h-5 bg-slate-100 dark:bg-slate-800 border-b border-slate-300 dark:border-slate-600 relative select-none">
            <div className="absolute left-0 top-0 w-5 h-5 bg-slate-200 dark:bg-slate-700 border-r border-b border-slate-300 dark:border-slate-600 z-10" />

            <div className="absolute left-5 top-0 right-0 h-full overflow-hidden">
                {ticks.map((tick, i) => (
                    <div
                        key={i}
                        className="absolute bottom-0 h-full"
                        style={{ left: `${tick.pos}px` }}
                    >
                        <div
                            className={`absolute w-px ${tick.isMajor ? 'h-3 bg-slate-500 dark:bg-slate-400' : 'h-1.5 bg-slate-400 dark:bg-slate-500'}`}
                            style={{ bottom: 0, left: 0 }}
                        />
                        {tick.label && (
                            <span
                                className="absolute text-[8px] text-slate-500 dark:text-slate-400 whitespace-nowrap"
                                style={{ top: 1, left: 2 }}
                            >
                                {tick.label}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function VerticalRuler({ length, scale, offset, containerSize }: RulerProps) {
    const ticks = useMemo(() => {
        const { major, minor } = getTickInterval(scale);
        const result: { pos: number; label: string | null; isMajor: boolean }[] = [];

        for (let mm = 0; mm <= length; mm += minor) {
            const isMajor = mm % major === 0;
            const pixelPos = offset + mm * scale;

            if (pixelPos >= -20 && pixelPos <= containerSize + 20) {
                result.push({
                    pos: pixelPos,
                    label: isMajor ? String(mm) : null,
                    isMajor
                });
            }
        }

        return result;
    }, [length, scale, offset, containerSize]);

    return (
        <div className="w-5 bg-slate-100 dark:bg-slate-800 border-r border-slate-300 dark:border-slate-600 relative select-none overflow-hidden">
            {ticks.map((tick, i) => (
                <div
                    key={i}
                    className="absolute right-0 w-full"
                    style={{ top: `${tick.pos}px` }}
                >
                    <div
                        className={`absolute h-px ${tick.isMajor ? 'w-3 bg-slate-500 dark:bg-slate-400' : 'w-1.5 bg-slate-400 dark:bg-slate-500'}`}
                        style={{ top: 0, right: 0 }}
                    />
                    {tick.label && (
                        <span
                            className="absolute text-[8px] text-slate-500 dark:text-slate-400 whitespace-nowrap"
                            style={{
                                top: 2,
                                left: 1,
                                writingMode: 'vertical-rl',
                                transform: 'rotate(180deg)',
                            }}
                        >
                            {tick.label}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}
